import {Injectable} from '@angular/core';
import {NewUser, User, UserId, UserPatch} from '../../shared/models/user';
import {HttpClient, HttpContext} from '@angular/common/http';
import {Observable, of, Subject} from 'rxjs';
import {SessionService} from './session.service';
import {API_BASE_URL} from './env.service';
import {UserInvitation} from '../../shared/models/invitations';
import {UserSettings} from '../../shared/models/user-settings';
import {SKIP_ERROR_REPORTING} from '../interceptors/app.interceptor';


@Injectable({
  providedIn: 'root',
})
export class UsersService {
  users: { [userId: UserId]: User } = {};

  private fetchingUser$Map: { [userId: UserId]: Subject<User> } = {};


  constructor(
    private http: HttpClient,
    private session: SessionService,
  ) {
  }


  /**
   *
   * @param newUser
   */
  createNewUser$(newUser: NewUser): Observable<User> {
    return this.http.post<User>(`${API_BASE_URL}/users`, newUser);
  }


  /**
   *
   * @param plaintext
   * @param userId
   */
  changePassword$(plaintext: string, userId?: UserId): Observable<any> {
    if (!userId) {
      userId = this.session.userId;
    }

    return this.http.put(
      `${API_BASE_URL}/users/${userId}/password`,
      JSON.stringify({
        Password: plaintext,
      }),
    );
  }


  patchUser$(userPatch: UserPatch, userId?: UserId): Observable<any> {
    return new Observable(
      subscriber => {
        this.http.patch(`${API_BASE_URL}/users/${userId}`, userPatch).subscribe(
          () => {
            this.session.refreshUserInfo$().subscribe();

            subscriber.next(true);
            subscriber.complete();
          },
          error => {
            subscriber.error(error);
          },
        );
      },
    );
  }


  /**
   * Get a user object when the user ID is known.
   *
   * @param userId
   */
  fetchUser$(userId: UserId): Observable<User> {
    if (this.fetchingUser$Map[userId]) {
      return this.fetchingUser$Map[userId];
    } else if (this.users[userId]) {
      return of(this.users[userId]);
    } else {
      this.fetchingUser$Map[userId] = new Subject<User>();

      this.http.get<User>(`${API_BASE_URL}/users/${userId}`).subscribe(
        (data) => {
          this.users[userId] = data;

          this.fetchingUser$Map[userId].next(data);
          this.fetchingUser$Map[userId].complete();

          delete this.fetchingUser$Map[userId];
        }, () => {
          this.fetchingUser$Map[userId].error(null);

          delete this.fetchingUser$Map[userId];
        },
      );

      return this.fetchingUser$Map[userId];
    }
  }


  /**
   * Get a user object when the user ID is *not* known.
   *
   * @param queryProperty
   * @param queryValue
   */
  findUser$(queryProperty: 'Username', queryValue: string): Observable<User> {
    const user$ = new Observable<User>(
      (subscriber) => {
        const params = {};
        params[queryProperty] = queryValue;

        return this.http.get<User[]>(`${API_BASE_URL}/users`, {
          params: params,
        }).subscribe(
          (users) => {
            if (users.length === 0) {
              let err = new Error('username not found');
              subscriber.error(err);
            } else {
              subscriber.next(users[0]);
              subscriber.complete();
            }
          },
          (error) => {
            subscriber.error(error);
          },
        );
      },
    );

    return user$;
  }


  createPasswordReset$(username: string): Observable<boolean> {
    return new Observable<boolean>(
      subscriber => {
        this.http.post(`${API_BASE_URL}/users/password-resets`, '', {
          params: {
            'Username': username,
          },
        }).subscribe(
          () => {
            subscriber.next(true);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  performPasswordReset$(resetCode: string, newPassword: string): Observable<boolean> {
    return new Observable<boolean>(
      subscriber => {
        this.http.post(`${API_BASE_URL}/users/password-resets/${resetCode}`, {
          'Password': newPassword,
        }).subscribe(
          () => {
            subscriber.next(true);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  createUserInvitation$(inviteeName: string, inviteeEmail: string): Observable<null> {
    return new Observable<null>(subscriber => {
      this.http.post(`${API_BASE_URL}/user-invitations`, {
        'InviteeName': inviteeName,
        'InviteeEmail': inviteeEmail,
      }).subscribe({
        next: () => {
          subscriber.next(null);
          subscriber.complete();
        },
        error: (err) => {
          subscriber.error(err);
        },
      });
    });
  }


  /**
   *
   */
  findUserInvitations$(): Observable<UserInvitation[]> {
    return new Observable<UserInvitation[]>(
      subscriber => {
        this.http.get<UserInvitation[]>(`${API_BASE_URL}/user-invitations`, {
          params: {
            'InviterID': this.session.userId,
          },
        }).subscribe({
          next: (invites) => {
            subscriber.next(invites);
            subscriber.complete();
          },
          error: (err) => {
            subscriber.error(err);
          },
        });
      },
    );
  }


  fetchUserInvitation$(inviteCode: string): Observable<UserInvitation> {
    return new Observable<UserInvitation>(
      subscriber => {
        // 404 means the invite code doesn't exist, handled as invalid-code by the caller rather than a failure
        this.http.get<UserInvitation>(`${API_BASE_URL}/user-invitations/${inviteCode}`, {
          context: new HttpContext().set(SKIP_ERROR_REPORTING, (err) => err.status === 404),
        }).subscribe(
          (invite) => {
            subscriber.next(invite);
            subscriber.complete();
          },
          (err) => {
            subscriber.error(err);
          },
        );
      },
    );
  }


  /**
   * Query for a set of users based on a confined list of supported query
   * properties:
   *    - AppID: gets all users who have any level of authorization to use a
   *      given app
   *
   * @param queryProperty
   * @param queryValue
   */
  findUsers$(queryProperty: 'AppID' | 'Email', queryValue: string): Observable<User[]> {
    const params = {};
    params[queryProperty] = queryValue;

    return this.http.get<User[]>(`${API_BASE_URL}/users`, {
      params: params,
    });
  }


  getUserSettings$(userId: UserId): Observable<UserSettings> {
    return this.http.get<UserSettings>(`${API_BASE_URL}/users/${userId}/settings`);
  }


  saveUserSettings$(userId: UserId, userSettings: UserSettings): Observable<UserSettings> {
    return this.http.put<UserSettings>(`${API_BASE_URL}/users/${userId}/settings`, userSettings);
  }
}
